'use strict';

/**
 * AJAX-фильтрация каталога.
 *
 * Перехватывает:
 *  - change на чекбоксах/радио → debounce 500ms → fetch
 *  - input на числовых полях (цена) → debounce 800ms → fetch
 *  - submit на кнопке «Применить» → немедленный fetch
 *  - клик по ссылкам сортировки/пагинации/per-page внутри #wa-content
 *
 * Поведение идентично реальному Shop-Script:
 *  - AJAX-запрос с X-Requested-With: XMLHttpRequest
 *  - Сервер возвращает только {$content} (без layout)
 *  - Клиент заменяет #wa-content innerHTML
 */
document.addEventListener('DOMContentLoaded', () => {
    const contentEl = document.getElementById('wa-content');
    if (!contentEl || !contentEl.querySelector('[data-catalog-filter]')) return;

    let debounceTimer = null;
    let isLoading = false;

    // ── Sidebar: auto-submit on change/input (instant filtering) ────────
    contentEl.addEventListener('change', (e) => {
        const form = e.target.closest('[data-filter-auto]');
        if (!form) return;
        if (e.target.matches('input[type=checkbox], input[type=radio], select')) {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => fetchFromForm(form), 500);
        }
    });

    contentEl.addEventListener('input', (e) => {
        const form = e.target.closest('[data-filter-auto]');
        if (!form) return;
        if (e.target.matches('input[type=number], input[type=range]')) {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => fetchFromForm(form), 800);
        }
    });

    // ── Dropdown / Drawer: submit only on explicit "Применить" click ──
    contentEl.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-catalog-filter]');
        if (!form) return;
        e.preventDefault();
        clearTimeout(debounceTimer);
        fetchFromForm(form);
    });

    // ── Event delegation: link clicks (sort, pagination, per-page) ─────
    contentEl.addEventListener('click', (e) => {
        const link = e.target.closest('a[href]');
        if (!link) return;

        const href = link.getAttribute('href');
        if (!href || !href.startsWith('?')) return;

        e.preventDefault();
        const isLoadMoreClick =
            !!link.closest('.catalog-pagination--loadmore') &&
            link.classList.contains('catalog-pagination__btn');

        // href="?" means reset — go to bare category URL
        if (href === '?') {
            fetchContent(location.pathname, true);
            return;
        }

        // Merge link params with current URL (preserve active filters)
        const linkParams = new URLSearchParams(href.slice(1));
        const currentParams = new URLSearchParams(window.location.search);

        for (const [k, v] of linkParams) {
            currentParams.set(k, v);
        }

        // Reset page when sort changes
        if (linkParams.has('sort')) {
            currentParams.delete('page');
        }

        const url = location.pathname + '?' + currentParams.toString();
        fetchContent(url, true, {
            appendProducts: isLoadMoreClick,
            appendMode: isLoadMoreClick ? 'loadmore' : '',
        });
    });

    // ── Browser back/forward ───────────────────────────────────────────
    window.addEventListener('popstate', () => {
        fetchContent(location.href, false);
    });

    // ── View toggle refresh (cookie was set, re-fetch content) ───────
    document.addEventListener('catalog-refresh', () => {
        fetchContent(location.href, false);
    });

    // ── Lazy pagination trigger (IntersectionObserver from Alpine) ────
    contentEl.addEventListener('catalog-load-next', () => {
        if (!contentEl.querySelector('.catalog-pagination--lazy')) return;

        const nextUrl = getNextPageUrl();
        if (!nextUrl) {
            syncLazyPaginationState();
            return;
        }

        fetchContent(nextUrl, false, {
            appendProducts: true,
            appendMode: 'lazy',
            replaceState: true,
        });
    });

    // ── Helpers ────────────────────────────────────────────────────────

    function updateProductsCount(root) {
        const target = root.querySelector('.js-products-count');
        if (!target) return;

        // Читаем из hidden-элемента внутри #catalog-main (обновляется AJAX)
        const meta = root.querySelector('#js-filtered-count');
        if (!meta) return;

        const onPage = parseInt(meta.dataset.count, 10) || 0;
        const pages  = parseInt(meta.dataset.pages, 10) || 1;
        const count  = pages <= 1 ? onPage : pages * 24;
        target.textContent = count;
    }

    function fetchFromForm(form) {
        const params = new URLSearchParams(new FormData(form));

        // Remove empty values and range slider duplicates
        for (const [k, v] of [...params]) {
            if (v === '' || v === '0') params.delete(k);
        }

        // Always reset to page 1 when filters change
        params.delete('page');

        const qs = params.toString();
        const action = form.getAttribute('action') || location.pathname;
        const base = (!action || action === '?') ? location.pathname : action;
        const url = base + (qs ? '?' + qs : '');
        fetchContent(url);
    }

    async function fetchContent(url, pushState = true, options = {}) {
        const {
            appendProducts = false,
            appendMode = '',
            replaceState = false,
        } = options;

        if (isLoading) return;
        isLoading = true;
        contentEl.classList.add('is-loading');

        try {
            const res = await fetch(url, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });

            if (!res.ok) throw new Error(res.status);

            const html = await res.text();

            // Reset body overflow (filter drawer sets it to 'hidden' and
            // gets destroyed by innerHTML replacement before calling hide())
            document.body.style.overflow = '';

            if (appendProducts) {
                const nextRoot = document.createElement('div');
                nextRoot.innerHTML = html;

                const currentProducts = getProductsContainer(contentEl);
                const nextProducts = getProductsContainer(nextRoot);

                if (currentProducts && nextProducts) {
                    const appendedNodes = [];
                    [...nextProducts.children].forEach(node => {
                        const clone = node.cloneNode(true);
                        currentProducts.appendChild(clone);
                        appendedNodes.push(clone);
                    });

                    updateCatalogStateFromUrl(url);

                    if (appendMode === 'loadmore') {
                        replacePagination(nextRoot);
                    } else if (appendMode === 'lazy') {
                        syncLazyPaginationState();
                    }

                    if (window.Alpine) {
                        appendedNodes.forEach(node => Alpine.initTree(node));
                        const pagination = contentEl.querySelector('.catalog-pagination');
                        if (pagination) Alpine.initTree(pagination);
                    }

                    if (replaceState) {
                        history.replaceState(null, '', url);
                    } else if (pushState) {
                        history.pushState(null, '', url);
                    }

                    // Notify subscribers that catalog URL has changed
                    window.dispatchEvent(new CustomEvent('catalog:updated', { detail: { url } }));
                    return;
                }
            }

            // Replace content
            contentEl.innerHTML = html;

            // Execute inline scripts (window.__CATALOG__ etc.)
            contentEl.querySelectorAll('script:not([src])').forEach(oldScript => {
                const newScript = document.createElement('script');
                newScript.textContent = oldScript.textContent;
                oldScript.parentNode.replaceChild(newScript, oldScript);
            });

            // Update products count from AJAX response
            updateProductsCount(contentEl);

            // Re-initialize Alpine.js on new content
            if (window.Alpine) {
                Alpine.initTree(contentEl);
            }

            // Re-initialize card photo swipers on new content
            if (window.initCardSwipers) {
                window.initCardSwipers();
            }

            // Update browser URL
            if (pushState) {
                history.pushState(null, '', url);
            }

            // Notify subscribers that catalog URL has changed (filters applied/removed etc.)
            window.dispatchEvent(new CustomEvent('catalog:updated', { detail: { url } }));

            // Scroll to top of content
            contentEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

        } catch (err) {
            console.error('Catalog AJAX error:', err);
            location.href = url; // fallback: full page reload
        } finally {
            isLoading = false;
            contentEl.classList.remove('is-loading');
        }
    }

    function getProductsContainer(root) {
        return root.querySelector('#catalog-products') || root.querySelector('.product-list-view');
    }

    function replacePagination(nextRoot) {
        const currentPagination = contentEl.querySelector('.catalog-pagination');
        const nextPagination = nextRoot.querySelector('.catalog-pagination');

        if (currentPagination && nextPagination) {
            currentPagination.replaceWith(nextPagination);
            return;
        }

        if (currentPagination && !nextPagination) {
            currentPagination.remove();
        }
    }

    function getCatalogMeta() {
        const params = new URLSearchParams(window.location.search);
        const fallbackPage = parseInt(params.get('page') || '1', 10) || 1;

        const currentPageRaw = Number(window.__CATALOG__?.currentPage);
        const totalPagesRaw = Number(window.__CATALOG__?.totalPages);

        const currentPage = Number.isFinite(currentPageRaw) && currentPageRaw > 0
            ? currentPageRaw
            : fallbackPage;

        const totalPages = Number.isFinite(totalPagesRaw) && totalPagesRaw > 0
            ? totalPagesRaw
            : currentPage;

        return { currentPage, totalPages };
    }

    function getNextPageUrl() {
        const { currentPage, totalPages } = getCatalogMeta();
        if (currentPage >= totalPages) return '';

        const params = new URLSearchParams(window.location.search);
        params.set('page', String(currentPage + 1));
        const qs = params.toString();
        return location.pathname + (qs ? '?' + qs : '');
    }

    function updateCatalogStateFromUrl(url) {
        const parsed = new URL(url, window.location.origin);
        const pageRaw = parseInt(parsed.searchParams.get('page') || '1', 10);
        const currentPage = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;

        window.__CATALOG__ = window.__CATALOG__ || {};

        const totalPagesRaw = Number(window.__CATALOG__.totalPages);
        const totalPages = Number.isFinite(totalPagesRaw) && totalPagesRaw > 0
            ? totalPagesRaw
            : currentPage;

        window.__CATALOG__.currentPage = currentPage;
        window.__CATALOG__.hasNextPage = currentPage < totalPages;
    }

    function syncLazyPaginationState() {
        const lazy = contentEl.querySelector('.catalog-pagination--lazy');
        if (!lazy) return;

        const { currentPage, totalPages } = getCatalogMeta();
        const hasNext = currentPage < totalPages;

        const sentinel = lazy.querySelector('.catalog-pagination__sentinel');
        if (sentinel) sentinel.hidden = !hasNext;

        const end = lazy.querySelector('.catalog-pagination__end');
        if (!hasNext) {
            if (!end) {
                const endEl = document.createElement('p');
                endEl.className = 'catalog-pagination__end';
                endEl.textContent = '';
                lazy.appendChild(endEl);
            }
        } else if (end) {
            end.remove();
        }
    }
});
